import {
  Box,
  Checkbox,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
} from "@mui/material";
import { toRgb } from "features/shared/lib/color";
import {
  dropdownCheckboxSx,
  dropdownMenuItemTextSx,
  getDropdownMenuItemSx,
  getDropdownMenuPropsSx,
  getMuiFilledSelectRootSx,
  muiFilledInputLabelSx,
  muiFilledSelectMultiWrapperClassName,
} from "features/shared/lib/muiDropdownStyles";
import { MUI_FILLED_FIELD, muiFilledInputLabelClasses } from "features/shared/lib/muiFilledFieldStyles";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { cn, resolveCssVar } from "utils/styling";

import { Icon } from "./Icon";

const BpIcon = styled("span")(() => ({
  backgroundColor: "var(--color-background-default)",
  border: "1px solid var(--color-foreground-strong)",
  borderRadius: "var(--border-radius-0-25x)",
  height: 16,
  "input:hover ~ &": {
    border: "1px solid var(--color-border-hover)",
    boxShadow: "var(--box-shadow-elevation-100)",
  },
  width: 16,
}));

const BpCheckedIcon = styled(BpIcon)(() => ({
  "&:before": {
    backgroundImage: `url("data:image/svg+xml;charset=utf-8,
        %3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E
        %3Cpath fill-rule='evenodd' clip-rule='evenodd'
        d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z'
        fill='rgb(${toRgb(resolveCssVar("var(--color-element-primary-initial)"))})'/%3E%3C/svg%3E")`,
    content: '""',
    display: "block",
    // TODO: Remove this workaround (17=16+1)
    height: 17,
    left: -1,
    position: "absolute",
    top: -1,
    width: 17,
  },

  backgroundColor: "var(--color-background-default)",
  border: "1px solid var(--color-border-accent)",
  borderRadius: "var(--border-radius-0-25x)",
  color: "var(--color-element-primary-initial)",
  "input:hover ~ &": {
    border: "1px solid var(--color-border-hover)",
  },
  position: "relative",
}));

const MenuItemComponent = ({ checked, displayOption, grouped, key, value }) => {
  return (
    <MenuItem key={key} sx={getDropdownMenuItemSx({ compact: true, grouped })} value={value}>
      <Checkbox
        checked={checked}
        checkedIcon={<BpCheckedIcon />}
        color="default"
        disableRipple
        icon={<BpIcon />}
        inputProps={{ "aria-label": "Checkbox demo" }}
        sx={dropdownCheckboxSx}
      />
      <ListItemText
        className="font-ds-body-regular-md"
        disableTypography
        primary={displayOption}
        sx={dropdownMenuItemTextSx}
      />
    </MenuItem>
  );
};

export const Dropdown = ({
  className,
  disabled,
  displayOptions,
  height = "4rem",
  label,
  onBlur,
  onChange,
  options,
  optionsGroups,
  selectProps,
  setValues,
  values,
  width,
  ...props
}) => {
  const isGrouped = optionsGroups?.length;
  if (!isGrouped) optionsGroups = [{ options }];
  const { t } = useTranslation();

  const mapValuesToDisplayValues = (values, options, displayOptions) => {
    return values?.map((value) => {
      return displayOptions[options.indexOf(value)];
    });
  };
  if (!displayOptions) displayOptions = options;

  const [isOpen, setIsOpen] = useState(false);
  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    if (value[value?.length - 1] === "all") {
      setValues(value?.length > options?.length ? [] : options);
    } else {
      setValues(typeof value === "string" ? value.split(",") : value);
    }
    if (onChange) onChange();
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const ChevronDown = (props) => {
    return (
      <svg
        {...props}
        fill="none"
        height="6"
        style={{
          cursor: "pointer",
          position: "absolute",
          right: "8%",
          top: "calc(50% - 0.3rem)",
          transform: isOpen ? "rotate(180deg)" : "none",
        }}
        viewBox="0 0 11 6"
        width="11"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1 0.750002L5.5 5.25L10 0.75"
          stroke="var(--color-foreground-strong)"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  const ref = useRef(null);

  const [openGroups, setOpenGroups] = useState({});
  const toggleGroup = (group) => {
    setOpenGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };
  const allOptionsAreSelected = options?.every((option) => values?.includes(option));

  return (
    <div
      className={cn(muiFilledSelectMultiWrapperClassName, className)}
      onClick={(e) => {
        e.stopPropagation();
      }}
      ref={ref}
      style={{ width }}
      {...props}
    >
      <FormControl sx={{ width: "100%" }} variant="filled">
        <InputLabel
          classes={{
            root: muiFilledInputLabelClasses.root,
            shrink: muiFilledInputLabelClasses.shrink,
          }}
          sx={muiFilledInputLabelSx}
        >
          {label}
        </InputLabel>
        <Select
          className="font-ds-body-regular-md"
          disabled={disabled}
          endAdornment={
            values?.length > 0 && (
              <InputAdornment className="mb-6 mr-1 w-0  " position="end">
                <Icon className="h-3 w-3" iconName="Erase" onClick={() => setValues([])} />
              </InputAdornment>
            )
          }
          IconComponent={ChevronDown}
          MenuProps={{
            sx: getDropdownMenuPropsSx(ref.current?.offsetWidth, "subtle"),
          }}
          multiple
          onBlur={onBlur}
          onChange={handleChange}
          onClose={handleClose}
          onOpen={handleOpen}
          renderValue={() => {
            if (allOptionsAreSelected) {
              return t("All");
            } else {
              return mapValuesToDisplayValues(values, options, displayOptions).join(", ");
            }
          }}
          sx={getMuiFilledSelectRootSx({
            hasValue: values?.length > 0,
            height,
          })}
          value={values}
          {...selectProps}
        >
          <MenuItem
            className={`mx-1 ${isGrouped && "ml-0 bg-lds-surface-muted hover:bg-lds-selection-strong"} `}
            sx={{
              ...getDropdownMenuItemSx({ compact: true, grouped: isGrouped }),
              backgroundColor: allOptionsAreSelected ? MUI_FILLED_FIELD.backgroundDefault : MUI_FILLED_FIELD.surfaceBright,
              padding: isGrouped ? "0.3rem 1.3rem" : "0.3rem 0",
            }}
            value="all"
          >
            <Checkbox
              checked={values?.length > 0 && allOptionsAreSelected}
              checkedIcon={<BpCheckedIcon />}
              color="default"
              disableRipple
              icon={<BpIcon />}
              inputProps={{ "aria-label": "Checkbox" }}
              sx={dropdownCheckboxSx}
            />
            <ListItemText
              className="!capitalize font-ds-body-regular-md"
              disableTypography
              primary={t("all")}
              sx={dropdownMenuItemTextSx}
            />
          </MenuItem>
          {optionsGroups?.map((group) => {
            const groupOptions = options?.filter((name) => group?.options?.includes(name));
            const groupDisplayOptions = displayOptions?.filter((_, index) => groupOptions?.includes(options[index]));
            const OptionsComponent = groupOptions?.map((name, index) => {
              return MenuItemComponent({
                checked: values?.indexOf(name) > -1,
                displayOption: groupDisplayOptions[index],
                grouped: isGrouped,
                key: index,
                value: name,
              });
            });
            if (isGrouped && groupOptions?.length)
              return [
                <ListSubheader className="w-full p-1" key={`header-${group.group}`}>
                  <Box
                    className={`${
                      openGroups[group.group] ? "bg-lds-selection-subtle " : "bg-lds-surface-subtle "
                    } flex w-full cursor-pointer items-center justify-between px-2 text-lds-foreground-strong hover:bg-lds-selection-strong`}
                    onClick={() => toggleGroup(group.group)}
                  >
                    <span>
                      <Checkbox
                        checked={groupOptions?.every((option) => values?.includes(option))}
                        checkedIcon={<BpCheckedIcon />}
                        color="default"
                        disableRipple
                        icon={<BpIcon />}
                        onChange={() => {
                          const isAllSelected = groupOptions?.every((option) => values?.includes(option));
                          if (isAllSelected) setValues(values?.filter((v) => !groupOptions?.includes(v)));
                          else {
                            setValues([...values, ...groupOptions]);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        sx={dropdownCheckboxSx}
                      />
                      <span className="ml-2  font-ds-body-bold-md">{group.group}</span>
                    </span>
                    <IconButton size="small">
                      {openGroups[group.group] ? <Icon iconName="ChevronDown" /> : <Icon iconName="ChevronRight" />}
                    </IconButton>
                  </Box>
                </ListSubheader>,
                openGroups[group.group] && OptionsComponent,
              ];
            else {
              return OptionsComponent;
            }
          })}
        </Select>
      </FormControl>
    </div>
  );
};
